import { axiosClient } from '../../../library/Service/axiosClient';
import PageConfigServiceProviderDashboard from './PageConfigServiceProviderDashboard.json';
import InquiryReportService from '../inquiry-management/InquiryReportService';

class ServiceProviderDashboardService {
    static pageLoader(params: any): Promise<any> {
        const state: any = {};
        const timeRange = 'THIS_MONTH';

        return Promise.all([
            this.getPageConfig(),
            this.getDashboard(timeRange),
        ]).then(([formConfig, dashboardResponse]) => {
            state.formConfig = formConfig;
            state.data = {
                provider: dashboardResponse?.provider || null,
                metrics: dashboardResponse?.metrics || this.getEmptyMetrics(),
                chartData: dashboardResponse?.chartData || [],
                funnel: dashboardResponse?.funnel || this.getEmptyFunnel(),
                recentInquiries: dashboardResponse?.recentInquiries || [],
                timeRange: dashboardResponse?.timeRange || timeRange,
                hasListing: !!dashboardResponse?.hasListing,
                isLoading: false,
            };
            state.routeInfo = params;
            state.internal = { gridState: {} };
            return Promise.resolve(state);
        }).catch((error) => {
            console.error('Error loading Service Provider Dashboard:', error);
            return Promise.resolve({
                formConfig: PageConfigServiceProviderDashboard,
                data: {
                    provider: null,
                    metrics: this.getEmptyMetrics(),
                    chartData: [],
                    funnel: this.getEmptyFunnel(),
                    recentInquiries: [],
                    timeRange,
                    hasListing: false,
                    isLoading: false,
                },
                routeInfo: params,
                internal: { gridState: {} },
            });
        });
    }

    static getPageConfig() {
        return Promise.resolve(PageConfigServiceProviderDashboard);
    }

    static getEmptyMetrics() {
        return {
            listingViews: 0,
            previousListingViews: 0,
            viewsTrendPct: 0,
            allTimeViews: 0,
            inquiriesReceived: 0,
            newInquiries: 0,
            awaitingResponse: 0,
            responded: 0,
            allTimeResponded: 0,
            responseRate: 0,
            avgResponseDays: 0,
            converted: 0,
            allTimeConverted: 0,
            conversionRate: 0,
            potentialValue: 0,
            comparisonLabel: 'last month',
        };
    }

    static getEmptyFunnel() {
        return {
            total: 0,
            responded: 0,
            converted: 0,
            respondedPct: 0,
            convertedPct: 0,
        };
    }

    static async getDashboard(timeRange: string) {
        const response = await axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/business-directory/provider-dashboard-report`,
            { timeRange }
        );

        return response?.data || {};
    }

    static async getProviderDetail(providerId: string) {
        const response = await axiosClient().get(`${process.env.REACT_APP_COMMON_API}/providers/${providerId}`);
        return response?.data || null;
    }

    static markInactive(providerId: string) {
        return axiosClient().delete(`${process.env.REACT_APP_COMMON_API}/providers/${providerId}`);
    }

    static updateProviderStatus(providerId: string, status: string) {
        return axiosClient().put(`${process.env.REACT_APP_COMMON_API}/providers/${providerId}`, { status });
    }

    static exportInquiryReportForTimeRange(timeRange: string) {
        const criteria = this.getInquiryCriteriaForTimeRange(timeRange);
        return InquiryReportService.exportCSV(criteria);
    }

    static getInquiryCriteriaForTimeRange(timeRange: string) {
        const today = new Date();
        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = `${date.getMonth() + 1}`.padStart(2, '0');
            const day = `${date.getDate()}`.padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        let from = new Date(today.getFullYear(), today.getMonth(), 1);
        let to = new Date(today);

        if (timeRange === 'THIS_WEEK') {
            const day = today.getDay();
            const diff = day === 0 ? -6 : 1 - day;
            from = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diff);
        } else if (timeRange === 'THIS_QUARTER') {
            const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
            from = new Date(today.getFullYear(), quarterStartMonth, 1);
        } else if (timeRange === 'THIS_YEAR') {
            from = new Date(today.getFullYear(), 0, 1);
        }

        return {
            search: '',
            status: '',
            outcome: '',
            fromDate: formatDate(from),
            toDate: formatDate(to),
            sort: 'newest',
            page: 1,
            limit: 20,
        };
    }
}

export default ServiceProviderDashboardService;